/**
 * EnterpriseLicenseTab.jsx
 * ========================
 * 
 * P0/P1 — Onglet complet de gestion des licences entreprise.
 * 
 * Intégré dans UserDashboard comme onglet "Entreprise".
 * Visible uniquement pour les admins d'entreprise.
 * 
 * FONCTIONNALITÉS:
 * - KPI: licences achetées / utilisées / disponibles
 * - Table des licences avec actions (révoquer)
 * - Attribution simple (email)
 * - Import en masse (CSV + textarea)
 * - Pagination + recherche
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  UserPlus,
  FileUp,
  Search,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader2,
  Mail,
  Calendar,
  Plus,
  UserCheck,
  Ban,
  Bell
} from 'lucide-react';
import { safeFetchJSON } from '../../utils/apiHelpers';
import { getBackendUrl } from '../../config/environment';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const BACKEND_URL = getBackendUrl();
const ITEMS_PER_PAGE = 10;

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

const EnterpriseLicenseTab = ({ companies = [] }) => {
  // États
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // États attribution
  const [showAttributeForm, setShowAttributeForm] = useState(false);
  const [attributeEmail, setAttributeEmail] = useState('');
  const [attributeLoading, setAttributeLoading] = useState(false);
  
  // États import bulk
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  
  // États demandes d'accès (P1)
  const [accessRequests, setAccessRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);

  // Auto-sélection première entreprise
  useEffect(() => {
    if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany]);

  // Charger données entreprise
  const loadCompanyData = useCallback(async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Charger stats, licences et demandes d'accès en parallèle
      const [statsData, licensesData, requestsData] = await Promise.all([
        safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/licenses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/access-requests?status=pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ requests: [] }))
      ]);
      
      setStats(statsData);
      setLicenses(licensesData.licenses || []);
      setAccessRequests(requestsData.requests || []);
    } catch (error) {
      console.error('Error loading company data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  // Filtrage et pagination
  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = license.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || license.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLicenses.length / ITEMS_PER_PAGE);
  const paginatedLicenses = filteredLicenses.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Attribution simple
  const handleAttributeLicense = async (e) => {
    e.preventDefault();
    if (!attributeEmail.trim()) {
      toast.error('Veuillez entrer un email');
      return;
    }

    setAttributeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_email: attributeEmail.trim() })
      });
      
      toast.success(`Licence attribuée à ${attributeEmail}`);
      setAttributeEmail('');
      setShowAttributeForm(false);
      loadCompanyData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'attribution');
    } finally {
      setAttributeLoading(false);
    }
  };

  // Import en masse
  const handleBulkImport = async () => {
    const emails = bulkEmails
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      toast.error('Aucun email valide trouvé');
      return;
    }

    setBulkLoading(true);
    setBulkResults(null);
    
    const results = { success: [], failed: [] };
    const token = localStorage.getItem('token');
    
    for (const email of emails) {
      try {
        await safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/licenses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ user_email: email })
        });
        results.success.push(email);
      } catch (error) {
        results.failed.push({ email, error: error.message });
      }
    }
    
    setBulkResults(results);
    setBulkLoading(false);
    
    if (results.success.length > 0) {
      toast.success(`${results.success.length} licence(s) attribuée(s)`);
      loadCompanyData();
    }
    if (results.failed.length > 0) {
      toast.error(`${results.failed.length} échec(s)`);
    }
  };

  // Révocation
  const handleRevoke = async (licenseId, userEmail) => {
    if (!window.confirm(`Révoquer la licence de ${userEmail} ?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await safeFetchJSON(`${BACKEND_URL}/api/company/${selectedCompany.id}/licenses/${licenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Licence révoquée');
      loadCompanyData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la révocation');
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const csvContent = [
      ['Email', 'Statut', 'Date création', 'Date expiration'].join(','),
      ...licenses.map(l => [
        l.user_email,
        l.status,
        l.created_at?.split('T')[0] || '',
        l.end_date?.split('T')[0] || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `licences_${selectedCompany?.name || 'export'}.csv`;
    a.click();
  };

  // P1: Traiter une demande d'accès
  const handleReviewRequest = async (requestId, action) => {
    try {
      const token = localStorage.getItem('token');
      await safeFetchJSON(
        `${BACKEND_URL}/api/company/${selectedCompany.id}/access-requests/${requestId}/review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action })
        }
      );
      
      toast.success(action === 'approve' ? 'Demande approuvée !' : 'Demande refusée');
      loadCompanyData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors du traitement');
    }
  };

  // Rendu si pas d'entreprises
  if (companies.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          Aucune entreprise
        </h3>
        <p className="text-gray-500 mb-6">
          Vous n&apos;êtes administrateur d&apos;aucune entreprise.
        </p>
        <Button
          onClick={() => window.location.href = '/enterprise-pricing'}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Souscrire une offre entreprise
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sélecteur d'entreprise (si plusieurs) */}
      {companies.length > 1 && (
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Entreprise :</label>
          <select
            value={selectedCompany?.id || ''}
            onChange={(e) => {
              const company = companies.find(c => c.id === e.target.value);
              setSelectedCompany(company);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
          >
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Licences achetées</p>
                <p className="text-3xl font-bold text-purple-700" data-testid="Licences-purchased">
                  {loading ? '...' : stats?.Licences?.total_purchased || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-200 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Utilisées</p>
                <p className="text-3xl font-bold text-blue-700" data-testid="Licences-used">
                  {loading ? '...' : stats?.Licences?.used || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-200 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">Disponibles</p>
                <p className="text-3xl font-bold text-emerald-700" data-testid="Licences-available">
                  {loading ? '...' : stats?.Licences?.available || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-200 flex items-center justify-center">
                <Loader2 className={`w-6 h-6 text-emerald-600 ${loading ? 'animate-spin' : ''}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 🎫 PHASE 3 MOA: Widget Pool Sessions Entreprise */}
      <Card className="bg-gradient-to-br from-violet-50 to-purple-100 border-violet-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-violet-200 flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-violet-800">Utilisation Sessions</h3>
                <p className="text-sm text-violet-600">Ce mois • Pool entreprise</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadCompanyData}
              className="text-violet-600 hover:text-violet-700"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Barre de progression globale - utilise l'état licenses (pas stats.licenses) */}
          {(() => {
            const activeLicenses = (licenses || []).filter(l => l.status === 'active');
            const totalQuota = activeLicenses.reduce((sum, l) => sum + (l.sessions_monthly_quota || 30), 0);
            const totalCurrent = activeLicenses.reduce((sum, l) => sum + (l.sessions_current ?? 30), 0);
            const totalUsed = totalQuota - totalCurrent;
            const usagePercent = totalQuota > 0 ? Math.round((totalUsed / totalQuota) * 100) : 0;
            
            return (
              <div>
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <p className="text-3xl font-bold text-violet-800">
                      {totalUsed}
                      <span className="text-lg font-normal text-violet-500 ml-1">/ {totalQuota}</span>
                    </p>
                    <p className="text-sm text-violet-600">sessions utilisées ce mois</p>
                  </div>
                  <p className="text-2xl font-semibold text-violet-700">{usagePercent}%</p>
                </div>
                <div className="h-3 bg-violet-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all"
                    style={{ width: `${Math.max(usagePercent, 2)}%` }}
                  />
                </div>
                <p className="text-xs text-violet-500 mt-2">
                  {activeLicenses.length} licence(s) active(s) • Renouvellement mensuel
                </p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* P1: Demandes d'accès en attente */}
      {accessRequests.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2 text-amber-800">
              <Bell className="w-5 h-5 text-amber-600" />
              Demandes d&apos;accès en attente
              <span className="ml-2 px-2 py-0.5 text-sm bg-amber-200 text-amber-800 rounded-full">
                {accessRequests.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {accessRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-amber-200"
                  data-testid={`access-request-${request.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{request.user_email}</p>
                      <p className="text-sm text-gray-500">
                        {request.user_name || 'Utilisateur'} • {new Date(request.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      {request.message && (
                        <p className="text-xs text-gray-400 mt-1 italic">&quot;{request.message}&quot;</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={() => handleReviewRequest(request.id, 'approve')}
                      data-testid={`approve-request-${request.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" />
                      Accepter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleReviewRequest(request.id, 'reject')}
                      data-testid={`reject-request-${request.id}`}
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Refuser
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions principales */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-purple-600" />
            Attribution de licences
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAttributeForm(!showAttributeForm)}
              data-testid="show-attribute-form-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email simple
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkImport(!showBulkImport)}
              data-testid="show-bulk-import-btn"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Import en masse
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Formulaire attribution simple */}
          <AnimatePresence>
            {showAttributeForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handleAttributeLicense}
                className="flex gap-3 p-4 bg-purple-50 rounded-xl border border-purple-200"
              >
                <Input
                  type="email"
                  placeholder="email@collaborateur.com"
                  value={attributeEmail}
                  onChange={(e) => setAttributeEmail(e.target.value)}
                  className="flex-1"
                  data-testid="attribute-email-input"
                />
                <Button
                  type="submit"
                  disabled={attributeLoading || !stats?.Licences?.available}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="attribute-license-btn"
                >
                  {attributeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Attribuer'
                  )}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Formulaire import en masse */}
          <AnimatePresence>
            {showBulkImport && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3"
              >
                <p className="text-sm text-blue-700">
                  Collez une liste d&apos;emails (1 par ligne, ou séparés par virgules)
                </p>
                <textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  placeholder="jean@entreprise.com&#10;marie@entreprise.com&#10;paul@entreprise.com"
                  className="w-full h-32 px-3 py-2 border border-blue-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
                  data-testid="bulk-emails-textarea"
                />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {bulkEmails.split(/[\n,;]/).filter(e => e.trim() && e.includes('@')).length} email(s) détecté(s)
                  </span>
                  <Button
                    onClick={handleBulkImport}
                    disabled={bulkLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="bulk-import-btn"
                  >
                    {bulkLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Import...
                      </>
                    ) : (
                      <>
                        <FileUp className="w-4 h-4 mr-2" />
                        Importer
                      </>
                    )}
                  </Button>
                </div>

                {/* Résultats import */}
                {bulkResults && (
                  <div className="mt-3 p-3 bg-white rounded-lg space-y-2">
                    {bulkResults.success.length > 0 && (
                      <p className="text-sm text-green-600 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {bulkResults.success.length} licence(s) attribuée(s)
                      </p>
                    )}
                    {bulkResults.failed.length > 0 && (
                      <div className="text-sm text-red-600">
                        <p className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4" />
                          {bulkResults.failed.length} échec(s):
                        </p>
                        <ul className="ml-6 text-xs">
                          {bulkResults.failed.slice(0, 5).map((f, i) => (
                            <li key={i}>{f.email}: {f.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Table des licences */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Licences attribuées ({filteredLicenses.length})
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadCompanyData}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filtres */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par email..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="license-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-4 py-2 border border-gray-200 rounded-lg"
              data-testid="status-filter-select"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="expired">Expirées</option>
              <option value="revoked">Révoquées</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : paginatedLicenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune licence attribuée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="licenses-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                    {/* 🎫 PHASE 3 MOA: Colonne Sessions pour admin */}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sessions</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Création</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Expiration</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedLicenses.map((license) => {
                    // 🎫 PHASE 3 MOA: Calcul des sessions
                    const sessionsQuota = license.sessions_monthly_quota || 30;
                    const sessionsCurrent = license.sessions_current ?? sessionsQuota;
                    const sessionsUsed = sessionsQuota - sessionsCurrent;
                    const sessionsPercent = Math.round((sessionsCurrent / sessionsQuota) * 100);
                    const isLow = sessionsPercent < 20;
                    
                    return (
                    <tr key={license.id} className="hover:bg-gray-50" data-testid={`license-row-${license.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-800">{license.user_email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={license.status} />
                      </td>
                      {/* 🎫 PHASE 3 MOA: Affichage sessions par licence */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-[80px]">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className={`font-medium ${isLow ? 'text-amber-600' : 'text-gray-600'}`}>
                                {sessionsCurrent}/{sessionsQuota}
                              </span>
                              {isLow && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                            </div>
                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  isLow ? 'bg-amber-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${Math.max(sessionsPercent, 3)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">
                        {license.created_at?.split('T')[0] || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">
                        {license.end_date?.split('T')[0] || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {license.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(license.id, license.user_email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`revoke-btn-${license.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">
                Page {currentPage} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// COMPOSANT STATUS BADGE
// =============================================================================

const StatusBadge = ({ status }) => {
  const config = {
    active: {
      icon: CheckCircle,
      label: 'Active',
      className: 'bg-green-100 text-green-700 border-green-200'
    },
    expired: {
      icon: Clock,
      label: 'Expirée',
      className: 'bg-orange-100 text-orange-700 border-orange-200'
    },
    revoked: {
      icon: XCircle,
      label: 'Révoquée',
      className: 'bg-red-100 text-red-700 border-red-200'
    }
  };

  const { icon: Icon, label, className } = config[status] || config.expired;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default EnterpriseLicenseTab;
